import type { Dispatch, FC } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useImage } from '@pflegenavi/frontend/api-nursing-home';
import {
  endOfDay,
  endOfMonth,
  endOfYear,
  isBefore,
  isSameDay,
  max,
  startOfDay,
  startOfMonth,
  startOfYear,
  subMonths,
  subYears,
} from 'date-fns';

import {
  Modal,
  ModalInner,
  useErrorSnackbar,
} from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import { pflegenaviBase64Logo } from './LogoBase64Encoded';
import { useNursingHomeContext } from '@pflegenavi/frontend/nursing-home-context';
import type { TransactionForReceiptImages } from '../utils/receiptImagesForPdf';
import { useReceiptImagesForPdf } from '../utils/receiptImagesForPdf';

import { prepareResidentOverviewData } from '../utils/prepareResidentOverviewData';
import { usePrintPdfForResident } from '../handlePrintPdfForResident';
import type { GetTransactionsWithReceiptDto } from '@pflegenavi/shared/api';
import { TransactionSourceType } from '@pflegenavi/shared/api';
import type { PdfResident } from '../interfaces/PdfResident';
import type { PendingCharge as ApiPendingCharge } from '@pflegenavi/frontend/api-nursing-home';

import { DownloadPdfButtonView } from './DownloadPdfButtonView';
import { ResidentPdfExportModalView } from './ResidentPdfExportModalView';

// Define the subset of PendingCharge properties needed for PDF export
type PdfPendingCharge = Pick<
  ApiPendingCharge,
  | 'id'
  | 'amount'
  | 'charge_date'
  | 'payment_initiation'
  | 'pending_charge_status'
  | 'source_of_mandate'
  | 'additional_data'
>;

// Minimal interface for pending receipts as returned by the backend
interface PendingReceiptSummary {
  id: string;
  amount: number;
  additional_fee_amount: number;
  included_fee_amount: number;
  additional_fees_diverted_to_pflegenavi: boolean;
  receipt_date: Date;
  inserted_at?: Date;
  receipt_state: string;
  created_by_employee_id: string;
  receipt_image_ids?: string[];
  service_provider?: {
    id: string;
    name: string;
    global_service_provider_id: string;
  };
  receipt_type?: {
    id: string;
    name: string;
    requires_service_provider: boolean;
  };
}

const isValidDate = (d: any): d is Date => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return d instanceof Date && !isNaN(d);
};

/**
 * Adapts a PendingReceiptSummary to TransactionForReceiptImages format.
 * Used to fetch receipt images for pending receipts in the PDF export.
 */
function adaptPendingReceiptToTransaction(
  receipt: PendingReceiptSummary & { receipt_date: Date },
  sequenceNumber: number
): TransactionForReceiptImages {
  return {
    id: receipt.id,
    amountInCent: receipt.amount,
    additionalFeeAmountInCent: receipt.additional_fee_amount,
    includedFeeAmountInCent: receipt.included_fee_amount,
    additionalFeesDivertedToPflegenavi:
      receipt.additional_fees_diverted_to_pflegenavi,
    receiptImageIds: receipt.receipt_image_ids,
    receiptDate: receipt.receipt_date,
    receiptType: receipt.receipt_type?.name,
    serviceProvider: receipt.service_provider?.name,
    date: receipt.receipt_date,
    sourceType: TransactionSourceType.Receipt,
    cancelled: false,
    sequenceNumber,
  };
}

interface DownloadPdfButtonProps {
  getTransactionsResident: (params: {
    dateFrom: Date;
    dateTo: Date;
  }) => Promise<GetTransactionsWithReceiptDto[]>;

  getResidentBalanceUntilDate(dataIn: {
    params: {
      residentId: string;
      familyMemberId?: string | null;
      date: Date;
    };
  }): Promise<{
    residentId: string;
    balance: number;
  }>;
  familyMemberId?: string | null;
  resident: PdfResident | undefined;
  isLoadingResident: boolean;
  showCancelledReceiptsCheckbox?: boolean;
  // Pending data should be provided by the calling component
  pendingReceipts: PendingReceiptSummary[] | undefined;
  pendingPayments: PdfPendingCharge[] | null;
}

export const DownloadPdfButton: FC<DownloadPdfButtonProps> = ({
  getResidentBalanceUntilDate,
  familyMemberId,
  isLoadingResident,
  resident,
  getTransactionsResident,
  showCancelledReceiptsCheckbox,
  pendingReceipts,
  pendingPayments,
}) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  return (
    <>
      <DownloadPdfButtonView
        onClick={() => setOpen(true)}
        disabled={isLoadingResident || !resident}
        loading={isLoadingResident}
        label={t('actions.download-pdf')}
      />
      {!isLoadingResident && resident && (
        <ResidentPdfExportModal
          // Re-mount if resident changes
          key={resident.id}
          open={open}
          setOpen={setOpen}
          getTransactionsResident={getTransactionsResident}
          getResidentBalanceUntilDate={getResidentBalanceUntilDate}
          familyMemberId={familyMemberId}
          resident={resident}
          showCancelledReceiptsCheckbox={showCancelledReceiptsCheckbox}
          pendingReceipts={pendingReceipts}
          pendingPayments={pendingPayments}
        />
      )}
    </>
  );
};

interface ResidentPdfExportModalProps {
  open: boolean;
  setOpen: Dispatch<boolean>;
  getTransactionsResident: (params: {
    dateFrom: Date;
    dateTo: Date;
  }) => Promise<GetTransactionsWithReceiptDto[]>;

  getResidentBalanceUntilDate(dataIn: {
    params: {
      residentId: string;
      familyMemberId?: string | null;
      date: Date;
    };
  }): Promise<{
    residentId: string;
    balance: number;
  }>;
  familyMemberId?: string | null;

  showCancelledReceiptsCheckbox?: boolean;

  resident: PdfResident;
  pendingReceipts: PendingReceiptSummary[] | undefined;
  pendingPayments: PdfPendingCharge[] | null;
}

const ResidentPdfExportModal: FC<ResidentPdfExportModalProps> = ({
  open,
  setOpen,
  getTransactionsResident,
  getResidentBalanceUntilDate,
  familyMemberId,
  resident,
  showCancelledReceiptsCheckbox,
  pendingReceipts,
  pendingPayments,
}) => {
  const { t } = useTranslation();
  const { enqueueErrorSnackbar } = useErrorSnackbar();

  const { getImagesIncludingPdfsFromReceiptIds } = useReceiptImagesForPdf();

  const [preparingPDF, setPreparingPDF] = useState(false);

  const { handlePrintPdfForResident } = usePrintPdfForResident();

  const initialStartDate = useMemo(() => {
    let startDate = startOfMonth(startOfDay(new Date()));
    if (
      resident.created_date &&
      startOfDay(resident.created_date) > startDate
    ) {
      startDate = startOfDay(resident.created_date);
    }
    return startDate;
  }, [resident.created_date]);

  // export options
  const [dateFrom, setDateFrom] = React.useState<Date | null>(initialStartDate);
  const [dateTo, setDateTo] = React.useState<Date | null>(endOfDay(new Date()));

  const { selectedNursingHome: nursingHome } = useNursingHomeContext();

  const { data: pdfLogo, isLoading: isImageLoading } = useImage(
    nursingHome?.pdfLogoId
  );
  const [includeReceiptImages, setIncludeReceiptImages] = useState(false);
  const [
    includeCancelledReceiptAttachments,
    setIncludeCancelledReceiptAttachments,
  ] = useState(false);

  const [isLoadingTransactions, setIsLoadingTransactions] = useState(true);
  const [relevantTransactions, setRelevantTransactions] = useState<
    GetTransactionsWithReceiptDto[]
  >([]);
  useEffect(() => {
    const abort = { current: false };
    setIsLoadingTransactions(true);
    getTransactionsResident({
      dateFrom: dateFrom ?? initialStartDate,
      dateTo: dateTo ?? endOfDay(new Date()),
    })
      .then((transactions) => {
        if (!abort.current) {
          setRelevantTransactions(transactions);
        }
      })
      .catch((err) => {
        enqueueErrorSnackbar(err, t('errors.something-went-wrong'));
      })
      .finally(() => {
        if (!abort.current) {
          setIsLoadingTransactions(false);
        }
      });

    return () => {
      // If the effect is re-computed, a new request is loading, so we don't want to set the loading state to false
      abort.current = true;
    };
  }, [
    enqueueErrorSnackbar,
    t,
    dateFrom,
    dateTo,
    getTransactionsResident,
    initialStartDate,
  ]);

  const residentOverviewData = useMemo(() => {
    return prepareResidentOverviewData(relevantTransactions);
  }, [relevantTransactions]);

  const hasCancelledReceipts = useMemo(() => {
    return residentOverviewData.withdrawals.some(
      (withdrawal) =>
        withdrawal.cancelled ||
        withdrawal.sourceType === TransactionSourceType.CancelledReceipt
    );
  }, [residentOverviewData.withdrawals]);

  const handleClickExport = useCallback(async () => {
    try {
      setPreparingPDF(true);
      if (dateFrom && dateTo) {
        const {
          amountOfDepositsCents,
          amountOfDepositAdditionalFeesCents,
          amountOfDepositIncludedFeesCents,
          numberOfDeposits,
          numberOfDepositCancellations,
          withdrawals,
          amountOfWithdrawalsCents,
          amountOfWithdrawalAdditionalFeesCents,
          amountOfWithdrawalIncludedFeesCents,
          numberOfWithdrawals,
          numberOfWithdrawalCancellations,
        } = residentOverviewData;

        // Get receipt attachments, rendering PDF pages as images so they embed
        // inline in the report.
        const receiptImages = includeReceiptImages
          ? await getImagesIncludingPdfsFromReceiptIds(withdrawals)
          : [];

        const receiptImagesWithoutCancelledAndCancellations =
          receiptImages.filter(
            (image) =>
              !image.transactionData.cancelled &&
              !image.transactionData.cancellation
          );

        const residentBalance = await getResidentBalanceUntilDate({
          params: {
            date: dateTo,
            familyMemberId: familyMemberId,
            residentId: resident.id,
          },
        });

        // Ensure pending receipts have proper Date objects for receipt_date
        const validatedPendingReceipts = pendingReceipts?.map((receipt) => ({
          ...receipt,
          receipt_date:
            receipt.receipt_date instanceof Date
              ? receipt.receipt_date
              : new Date(receipt.receipt_date),
        }));

        // Get pending receipt images (including PDF pages rendered as images)
        // Reverse transaction order before image expansion so that
        // multi-page PDF pages stay in the correct order within each receipt.
        const pendingReceiptImages = includeReceiptImages
          ? await getImagesIncludingPdfsFromReceiptIds(
              (validatedPendingReceipts ?? [])
                .map((r, index, arr) => ({
                  receipt: r,
                  position: arr.length - index,
                }))
                .filter((r) => r.receipt.receipt_image_ids?.length)
                .map(({ receipt, position }) =>
                  adaptPendingReceiptToTransaction(receipt, position)
                )
                .reverse()
            )
          : [];

        await handlePrintPdfForResident({
          dateFrom,
          dateTo,
          resident,
          amountOfDepositsCents,
          amountOfDepositAdditionalFeesCents,
          amountOfDepositIncludedFeesCents,
          amountOfWithdrawalsCents,
          amountOfWithdrawalAdditionalFeesCents,
          amountOfWithdrawalIncludedFeesCents,
          numberOfWithdrawals,
          numberOfDeposits,
          numberOfDepositCancellations,
          numberOfWithdrawalCancellations,
          receiptImages: includeCancelledReceiptAttachments
            ? receiptImages
            : receiptImagesWithoutCancelledAndCancellations,
          balanceEndCents: residentBalance?.balance ?? 0,
          logo: pdfLogo ? pdfLogo.url : pflegenaviBase64Logo,
          allEntries: relevantTransactions,
          pendingReceipts: validatedPendingReceipts,
          pendingPayments: pendingPayments,
          pendingReceiptImages,
        });
        setOpen(false);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);

      enqueueErrorSnackbar(e, t('errors.something-went-wrong'), {
        variant: 'error',
      });
    } finally {
      setPreparingPDF(false);
    }
  }, [
    familyMemberId,
    relevantTransactions,
    includeCancelledReceiptAttachments,
    resident,
    dateFrom,
    dateTo,
    residentOverviewData,
    includeReceiptImages,
    getImagesIncludingPdfsFromReceiptIds,
    getResidentBalanceUntilDate,
    handlePrintPdfForResident,
    pdfLogo,
    setOpen,
    enqueueErrorSnackbar,
    t,
    pendingReceipts,
    pendingPayments,
  ]);

  const handleClickLastMonth = useCallback(() => {
    const startDates = [startOfMonth(subMonths(startOfDay(new Date()), 1))];
    if (resident.created_date) {
      startDates.push(resident.created_date);
    }

    setDateFrom(max(startDates));
    setDateTo(endOfMonth(subMonths(endOfDay(new Date()), 1)));
  }, [resident]);

  const lastMonthButtonActive = useMemo(() => {
    return !!(
      dateTo &&
      dateFrom &&
      isSameDay(dateTo, endOfMonth(subMonths(new Date(), 1))) &&
      isSameDay(dateFrom, startOfMonth(subMonths(new Date(), 1)))
    );
  }, [dateFrom, dateTo]);

  const handleClickLastYear = useCallback(() => {
    const startDates = [startOfYear(subYears(startOfDay(new Date()), 1))];
    if (resident.created_date) {
      startDates.push(resident.created_date);
    }

    setDateFrom(max(startDates));
    setDateTo(endOfYear(subYears(endOfDay(new Date()), 1)));
  }, [resident]);

  const lastYearButtonActive = useMemo(() => {
    return !!(
      dateTo &&
      dateFrom &&
      isSameDay(dateTo, endOfYear(subYears(new Date(), 1))) &&
      isSameDay(startOfYear(subYears(new Date(), 1)), dateFrom)
    );
  }, [dateFrom, dateTo]);

  const residentCreatedDateOlderThanCurrentMonth = useMemo(() => {
    if (resident) {
      return isBefore(resident.created_date, startOfMonth(new Date()));
    }
    return false;
  }, [resident]);

  const residentCreatedDateOlderThanCurrentYear = useMemo(() => {
    if (resident) {
      return isBefore(resident.created_date, startOfYear(new Date()));
    }
    return false;
  }, [resident]);

  const dateError = useMemo(() => {
    if (dateFrom && dateTo) {
      if (dateFrom >= dateTo) {
        return t('resident-pdf.modal.date-error');
      } else if (startOfDay(dateTo) > startOfDay(new Date())) {
        return t('resident-pdf.modal.date-in-future-error');
      }
    }
    if (dateFrom && dateFrom < startOfDay(resident.created_date)) {
      return t('resident-pdf.modal.date-in-before-resident-entry-error');
    }
    return undefined;
  }, [dateFrom, dateTo, resident, t]);

  const handleDateFromChange = useCallback((newValue: Date | null) => {
    if (isValidDate(newValue)) {
      setDateFrom(newValue ? startOfDay(newValue) : newValue);
    } else {
      setDateFrom(null);
    }
  }, []);

  const handleDateToChange = useCallback((newValue: Date | null) => {
    if (isValidDate(newValue)) {
      setDateTo(endOfDay(newValue));
    } else {
      setDateTo(null);
    }
  }, []);

  return (
    <Modal open={open} handleClose={() => setOpen(false)}>
      <ModalInner>
        <ResidentPdfExportModalView
          onClose={() => setOpen(false)}
          onExport={handleClickExport}
          onLastMonth={handleClickLastMonth}
          onLastYear={handleClickLastYear}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={handleDateFromChange}
          onDateToChange={handleDateToChange}
          includeReceiptImages={includeReceiptImages}
          onIncludeReceiptImagesChange={setIncludeReceiptImages}
          includeCancelledReceiptAttachments={
            includeCancelledReceiptAttachments
          }
          onIncludeCancelledReceiptAttachmentsChange={
            setIncludeCancelledReceiptAttachments
          }
          isLoadingTransactions={isLoadingTransactions}
          preparingPDF={preparingPDF}
          isImageLoading={isImageLoading}
          residentCreatedDate={resident.created_date}
          lastMonthButtonActive={lastMonthButtonActive}
          lastYearButtonActive={lastYearButtonActive}
          residentCreatedDateOlderThanCurrentMonth={
            residentCreatedDateOlderThanCurrentMonth
          }
          residentCreatedDateOlderThanCurrentYear={
            residentCreatedDateOlderThanCurrentYear
          }
          hasCancelledReceipts={hasCancelledReceipts}
          showCancelledReceiptsCheckbox={showCancelledReceiptsCheckbox}
          dateError={dateError}
          title={t('resident-pdf.modal.title')}
          pleaseSelectRange={t('resident-pdf.modal.please-select-range')}
          fromLabel={t('resident-pdf.modal.from')}
          toLabel={t('resident-pdf.modal.to')}
          lastMonthLabel={t('resident-pdf.modal.last-month')}
          lastYearLabel={t('resident-pdf.modal.last-year')}
          receiptOptionsLabel={t('resident-pdf.modal.receipt-options')}
          includeReceiptImagesLabel={t(
            'resident-pdf.modal.include-receipt-images'
          )}
          includeCancelledReceiptAttachmentsLabel={t(
            'resident-pdf.modal.include-cancelled-receipt-attachments'
          )}
          cancelLabel={t('resident-pdf.modal.cancel')}
          exportLabel={t('resident-pdf.modal.export')}
        />
      </ModalInner>
    </Modal>
  );
};
